import type { AwsRum } from 'aws-rum-web';
import * as React from 'react';

/**
 * Instrumentation context for AWS RUM (Real User Monitoring) integration.
 *
 * **TESTING GUIDANCE** (MoSCoW Priority: SHOULD):
 * This context and its hooks SHOULD be used directly in tests rather than mocked.
 * The instrumentation system is internal application logic that benefits from integration testing.
 *
 * **Context Purpose**:
 * - Provides AWS RUM client for application monitoring
 * - Enables performance tracking and error reporting
 * - Manages RUM client lifecycle and configuration
 * - Supports optional RUM integration (graceful degradation when unavailable)
 *
 * **Testing Setup**:
 * ```typescript
 * // RECOMMENDED: Use real InstrumentationProvider in tests
 * import { InstrumentationProvider } from '../context/InstrumentationProvider';
 *
 * const renderWithInstrumentation = (component) => {
 *   return render(
 *     <InstrumentationProvider>
 *       {component}
 *     </InstrumentationProvider>
 *   );
 * };
 *
 * // Test instrumentation integration
 * const { getByTestId } = renderWithInstrumentation(<YourComponent />);
 * // Component should handle null rumClient gracefully
 * expect(getByTestId('component')).toBeInTheDocument();
 * ```
 *
 * **Why Use Directly**:
 * - Tests proper handling of null/undefined RUM client
 * - Verifies graceful degradation when RUM is unavailable
 * - Ensures components don't break without instrumentation
 * - Tests integration between components and monitoring system
 *
 * **External Dependencies to Mock**:
 * - AWS RUM Web SDK (already handled by context providing null client in tests)
 *
 * **Note**: The context typically provides `rumClient: null` in test environments,
 * which is the expected behavior for testing graceful degradation.
 *
 * @see {@link useInstrumentation} Hook for accessing RUM client
 */
export interface IInstrumentation {
  rumClient: AwsRum | null;
}

export const InstrumentationContext = React.createContext<IInstrumentation>({ rumClient: null });

/**
 * Use the InstrumentationContext
 * @returns IInstrumentation
 */
export function useInstrumentation(): IInstrumentation {
  return React.useContext(InstrumentationContext);
}
